import type { MarketDataSourceOption } from "../types/marketDataSources";
import type { DemoCandleTimeframe } from "../types/demoEntryAnalysis";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import {
  defaultMarketDataSourceOption,
  inferBinanceProductLane,
  marketDataBrokerKey,
} from "./marketDataSourceChoice";
import type { QuoteVariantCandidate } from "../types/instrumentSetup";
import {
  findQuoteVariantCandidate,
  inferQuoteVariantProductLane,
} from "./instrumentSetupSave";

/** Demo OHLC vendors — Tessera central lanes only (no BYOK broker feeds). */
export const DEMO_CENTRAL_OHLC_BROKERS = new Set([
  "twelvedata",
  "coinapi",
  "hyperliquid",
  "databento",
]);

export function isDemoCentralOhlcBroker(broker?: string | null): boolean {
  const normalized = (broker ?? "").trim().toLowerCase();
  return normalized !== "" && DEMO_CENTRAL_OHLC_BROKERS.has(normalized);
}

export function filterDemoCentralMarketDataSources(
  sources: MarketDataSourceOption[] | null | undefined
): MarketDataSourceOption[] {
  if (!sources?.length) return [];
  return sources.filter((source) => isDemoCentralOhlcBroker(marketDataBrokerKey(source)));
}

/** Strip BYOK OHLC lanes from cohort / resolve rows (defense in depth vs cached BE). */
export function applyDemoCentralInstrumentRow<T extends AvailableInstrumentRow>(row: T): T {
  const market_data_sources = filterDemoCentralMarketDataSources(row.market_data_sources);
  const candidate_brokers = (row.candidate_brokers ?? []).filter((candidate) =>
    isDemoCentralOhlcBroker(candidate.broker)
  );

  const selectedIsCentral =
    row.selected_market_data_source &&
    isDemoCentralOhlcBroker(marketDataBrokerKey(row.selected_market_data_source));
  const selected_market_data_source = selectedIsCentral
    ? row.selected_market_data_source
    : defaultMarketDataSourceOption(market_data_sources) || market_data_sources[0] || null;

  const rowBrokerCentral = isDemoCentralOhlcBroker(row.market_data_broker);
  const fallback = selected_market_data_source || market_data_sources[0] || null;

  return {
    ...row,
    market_data_sources,
    candidate_brokers,
    selected_market_data_source,
    market_data_broker: rowBrokerCentral
      ? row.market_data_broker
      : fallback?.market_data_broker ?? fallback?.broker ?? row.market_data_broker ?? null,
    md_listing_key: rowBrokerCentral
      ? row.md_listing_key
      : fallback?.md_listing_key ?? row.md_listing_key ?? null,
  };
}

export function applyDemoCentralInstrumentRows(
  instruments: AvailableInstrumentRow[]
): AvailableInstrumentRow[] {
  return instruments.map((row) => applyDemoCentralInstrumentRow(row));
}

/** Align central OHLC listing with quote/perp lane (CoinAPI venue keys, Hyperliquid perps, …). */
export function pickDemoMarketDataSourceForQuoteLane(
  sources: MarketDataSourceOption[],
  quoteVariant: string,
  quoteCandidates: QuoteVariantCandidate[]
): MarketDataSourceOption | null {
  if (!sources.length) return null;

  const lane =
    inferQuoteVariantProductLane(
      findQuoteVariantCandidate(quoteCandidates, quoteVariant) || quoteVariant
    ) || undefined;

  if (!lane) {
    return defaultMarketDataSourceOption(sources) || sources[0] || null;
  }

  if (lane === "futures") {
    const hyperliquid = sources.find((source) => marketDataBrokerKey(source) === "hyperliquid");
    if (hyperliquid) return hyperliquid;
  }

  const byLane = sources.find((source) => inferBinanceProductLane(source) === lane);
  if (byLane) return byLane;

  return defaultMarketDataSourceOption(sources) || sources[0] || null;
}

/** Databento demo lane is US futures — BE only supports 1h candles there. */
export function isDemoDatabentoFuturesListing(input: {
  marketDataBroker?: string | null;
  source?: Pick<MarketDataSourceOption, "broker" | "market_data_broker"> | null;
}): boolean {
  const broker =
    marketDataBrokerKey(input.source ?? {}) ||
    (input.marketDataBroker ?? "").trim().toLowerCase();
  return broker === "databento";
}

export function demoCandleTimeframeOptions(
  databentoListing: boolean
): Array<{ value: DemoCandleTimeframe; label: string }> {
  if (databentoListing) {
    return [{ value: "1h", label: "1h" }];
  }
  return [
    { value: "1h", label: "1h" },
    { value: "30m", label: "30m" },
  ];
}

export function normalizeDemoCandleTimeframe(
  timeframe: DemoCandleTimeframe,
  databentoListing: boolean
): DemoCandleTimeframe {
  if (databentoListing && timeframe === "30m") return "1h";
  return timeframe;
}

export function demoCandleTimeframeFieldDescription(databentoListing: boolean): string {
  if (databentoListing) {
    return "Choose the candle size for market context. US futures use only 1h for this demo.";
  }
  return "Choose the candle size for market context. Demo reports use 1h or 30m only.";
}
